/**
 * Resolve the location NAME to display for a session row (matches the
 * appointment flow's labelling). Returns undefined when no location
 * resolves — the session card then renders no location pin at all.
 *
 * Resolution order:
 * 1. session.locationName — explicit per-session override
 * 2. session.locationId → locations lookup → location.name
 * 3. legacy: session.location as a bare location id string → location.name
 */
export function resolveSessionLocationName(
  session: any,
  locations: any[],
): string | undefined {
  if (session.locationName) return session.locationName;
  if (locations.length === 0) return undefined;

  const findById = (id: string) =>
    locations.find((loc: any) => loc._id === id || loc.id === id);

  if (session.locationId) {
    const location = findById(session.locationId);
    if (location) return location.name || undefined;
  }

  // Legacy: session.location may hold a bare location id string
  if (session.location && typeof session.location === "string") {
    const location = findById(session.location);
    if (location) return location.name || undefined;
  }

  return undefined;
}

/**
 * Resolve the venue NAME for the checkout sidebar summary, which is on screen
 * from the very first step — before any session has been picked.
 *
 * Resolution order:
 * 1. the selected session's already-resolved location name
 * 2. the listing's only venue, when the whole schedule runs at one place
 * 3. undefined — the summary card falls back to a generic "In-person"
 *
 * Online listings and online sessions have no venue to name.
 *
 * Step 2 reads `course.summary.locationIds`, which is scoped to THIS listing's
 * upcoming sessions, so a single entry really does mean every session is at
 * that one venue. Don't swap it for the school-wide `locations` array — a
 * one-venue school can still run a listing whose sessions name no venue at all.
 */
export function resolveSummaryLocationName(
  course: any,
  locations: any[],
  selectedSession?: { isOnline?: boolean; location?: string } | null,
): string | undefined {
  if (course?.isOnline || selectedSession?.isOnline) return undefined;
  if (selectedSession?.location) return selectedSession.location;

  const locationIds: string[] = course?.summary?.locationIds ?? [];
  if (locationIds.length !== 1) return undefined;

  const onlyId = locationIds[0];
  const location = locations.find(
    (loc: any) => loc._id === onlyId || loc.id === onlyId,
  );
  return location?.name || undefined;
}
